import React, { useEffect, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Line } from 'react-chartjs-2';
import { usePricing } from '../contexts/PricingContext';
import { useDemoMode } from '../contexts/DemoModeContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
);

export const LiveChart = () => {
  const { btcPrice, isLive, dataSource } = usePricing();
  const { isDemoMode, toggleDemoMode } = useDemoMode();
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: []
  });
  const priceHistoryRef = useRef([]);
  const lastPriceRef = useRef(null);
  const chartRef = useRef(null);

  const formatPrice = (price) => {
    if (!price) return '$0';
    return `$${Number(price).toLocaleString('en-US', { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    })}`;
  };

  // Initialize with base price
  useEffect(() => {
    if (btcPrice && priceHistoryRef.current.length === 0) {
      console.log('📈 Chart.js: Initializing with base price:', btcPrice);
      
      const now = Date.now();
      const initialData = [];
      let price = btcPrice - 100;
      
      for (let i = 59; i >= 0; i--) {
        const timestamp = now - (i * 1000);
        price += (Math.random() - 0.5) * 20;
        price = Math.max(price, btcPrice - 200);
        price = Math.min(price, btcPrice + 200);
        
        initialData.push({
          x: timestamp,
          y: price
        });
      }
      
      priceHistoryRef.current = initialData;
      lastPriceRef.current = price;
      updateChart(initialData);
    }
  }, [btcPrice]);

  // Smooth price updates
  useEffect(() => {
    if (btcPrice && priceHistoryRef.current.length > 0) {
      const currentTime = Date.now();
      const lastPrice = lastPriceRef.current || btcPrice;
      const priceChange = Math.abs(btcPrice - lastPrice);
      
      if (priceChange > 0.01) {
        console.log('📈 Chart.js: Smooth update from', formatPrice(lastPrice), 'to', formatPrice(btcPrice));
        
        const newPoint = {
          x: currentTime,
          y: btcPrice
        };
        
        const newHistory = [...priceHistoryRef.current, newPoint].slice(-120);
        priceHistoryRef.current = newHistory;
        lastPriceRef.current = btcPrice;
        updateChart(newHistory);
      }
    }
  }, [btcPrice]);

  const updateChart = (data) => {
    if (data.length === 0) return;
    
    const prices = data.map(d => d.y);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRange = maxPrice - minPrice;
    const padding = priceRange * 0.1;
    
    setChartData({
      datasets: [{
        label: 'BTC/USD',
        data: data,
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 4,
        cubicInterpolationMode: 'monotone',
      }]
    });

    if (chartRef.current) {
      chartRef.current.options.scales.y.min = minPrice - padding;
      chartRef.current.options.scales.y.max = maxPrice + padding;
      chartRef.current.update('none');
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            return `BTC: ${formatPrice(context.parsed.y)}`;
          }
        }
      },
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'second',
          displayFormats: {
            second: 'HH:mm:ss'
          }
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          maxTicksLimit: 8
        }
      },
      y: {
        position: 'left',
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          callback: function(value) {
            return formatPrice(value);
          },
          padding: 8,
          maxTicksLimit: 8
        }
      }
    },
  };

  return (
    <div className="bg-atticus-navy-900">
      <div className="flex items-center justify-between px-6 py-4 border-b border-atticus-navy-700">
        <div className="flex items-center space-x-2">
          <h2 className="text-white text-xl font-bold">BTC/USD:</h2>
          <span className="text-white text-xl font-bold">{formatPrice(btcPrice)}</span>
        </div>
        
        <div className="flex items-center space-x-2">
          {/* Mobile: Segmented Control */}
          <div className="flex bg-gray-700 rounded-md p-0.5 sm:hidden">
            <button
              className={`px-3 py-1 text-xs font-bold rounded transition-colors ${
                isDemoMode ? 'bg-orange-600 text-white shadow-sm' : 'text-gray-300 hover:text-white'
              }`}
            >
              DEMO
            </button>
            <button
              onClick={() => alert('Live trading coming soon! Platform currently in demo mode.')}
              className={`px-3 py-1 text-xs font-bold rounded transition-colors ${
                !isDemoMode ? 'bg-green-600 text-white shadow-sm' : 'text-gray-300 hover:text-white'
              }`}
            >
              LIVE
            </button>
          </div>

          {/* Desktop: Original Oval Toggle */}
          <div className="hidden sm:flex items-center space-x-2">
            <span className={`text-sm font-medium ${isDemoMode ? 'text-orange-400' : 'text-green-400'}`}>
              {isDemoMode ? 'DEMO' : 'LIVE'}
            </span>
            <button
              onClick={() => {
                if (isDemoMode) {
                  alert('Live trading coming soon! Platform currently in demo mode.');
                } else {
                  toggleDemoMode();
                }
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isDemoMode ? 'bg-orange-600' : 'bg-green-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isDemoMode ? 'translate-x-1' : 'translate-x-6'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      <div className="h-96 p-4 bg-[#1a1e2e]">
        <Line 
          ref={chartRef}
          data={chartData} 
          options={chartOptions} 
        />
      </div>
    </div>
  );
};
